/**
 * Cluster 4 · Return on investment
 *
 * Contains: ROI cluster header (TeachingLearningCluster variant="roi"),
 * Placements & practical experience, Graduate outcomes (with the big
 * employer chip grid), and the SimilarCoursesLink footer.
 *
 * Extracted verbatim from DegreeDetail.jsx (Feb 2026 refactor).
 */
import { Link } from "react-router-dom";
import { ClusterMember } from "@/components/app/degree/ClusterLayout";
import { Section, DataRow, Ev } from "@/components/app/degree/DegreeDetailBits";
import { SimilarCoursesLink } from "@/components/app/degree/DegreeInterlinks";
import { AIInterpretationNote } from "@/components/app/AIInterpretationCard";
import TeachingLearningCluster from "@/components/app/degree/TeachingLearningCluster";
import { ScopeChip, OutcomeScopeLegend } from "@/components/app/degree/ScopeChip";
import SalaryProgressionWidget from "@/components/app/SalaryProgressionWidget";
import FeeVsSalaryCard from "@/components/app/degree/FeeVsSalaryCard";
import { SmallSample } from "@/components/app/degree/HesaAdmissionsSnapshot";
import DegreeEventsInline from "@/components/app/degree/DegreeEventsInline";
import PlacementsDeepDiveCard from "@/components/app/PlacementsDeepDiveCard";
import { LeoComparison, TopGraduateJobs } from "@/components/app/degree/HesaPhase1Bits";
import { EmploymentBreakdownBlock } from "@/components/app/degree/HesaPhase3Bits";
import UsEarningsComparison from "@/components/app/degree/UsEarningsComparison";
import DeSubjectBaseline from "@/components/app/degree/DeSubjectBaseline";
import { UsSubjectBaseline } from "@/components/app/degree/UsInstitutionSignals";
import LeoSectorChart from "@/components/app/degree/LeoSectorChart";
import PostgradSiblingsWidget from "@/components/app/degree/PostgradSiblingsWidget";
import DegreeSourcesPanel from "@/components/app/DegreeSourcesPanel";
import ChartVerdict from "@/components/app/degree/ChartVerdict";
import SubjectAggregatePanel from "@/components/app/degree/SubjectAggregatePanel";
import FirmLogo from "@/components/app/FirmLogo";
import { OfficialEarningsNotice } from "@/components/app/degree/OfficialEarningsNotice";
import { IntlEarningsPanel } from "@/components/app/degree/IntlEarningsPanel";
import { ExternalLink, ShieldCheck, Briefcase } from "lucide-react";
import { firstYearOnly } from "@/lib/feeYear";

// -----------------------------------------------------------------
//  Tiny helpers for building the one-line ROI chart verdicts. Kept
//  local so any calling site can pass shape-specific data in and
//  get back a pre-formatted "so what?" caption string.
// -----------------------------------------------------------------
function _fmtGBP(v) {
  if (v == null || Number.isNaN(v)) return null;
  return `£${Math.round(v).toLocaleString("en-GB")}`;
}
function _fmtUSD(v) {
  if (v == null || Number.isNaN(v)) return null;
  return `$${Math.round(v).toLocaleString("en-US")}`;
}

/** LEO sector chart: compare highest vs lowest region median. */
function leoSectorVerdict(leoSec) {
  const dataset = leoSec?.leo5 || leoSec?.leo3 || leoSec?.go || {};
  const rows = Object.entries(dataset)
    .filter(([k, v]) => k !== "UK" && v && v.med != null)
    .map(([k, v]) => ({ code: k, med: v.med }));
  if (rows.length < 2) return null;
  rows.sort((a, b) => b.med - a.med);
  const top = rows[0];
  const bot = rows[rows.length - 1];
  const delta = top.med - bot.med;
  if (delta <= 500) {
    return { text: "Regional pay is broadly flat for this subject — location isn't the big driver of early-career earnings.", tone: "neutral" };
  }
  const pct = Math.round((delta / bot.med) * 100);
  const REGION_LABEL = {
    LON: "London", SE: "the South East", SW: "the South West",
    EE: "the East of England", EM: "the East Midlands", WM: "the West Midlands",
    YH: "Yorkshire & Humber", NE: "the North East", NW: "the North West",
    SCO: "Scotland", WAL: "Wales", NIR: "Northern Ireland",
  };
  return {
    text: `Working in ${REGION_LABEL[top.code] || top.code} lifts pay ~${_fmtGBP(delta)} (${pct}%) above ${REGION_LABEL[bot.code] || bot.code} — the biggest regional gap in this subject.`,
    tone: pct >= 20 ? "warning" : "neutral",
  };
}

/** Progression widget: 15-month → 3y salary climb. */
function progressionVerdict(outcomes, isUK) {
  const start = outcomes?.median_starting_salary_usd;
  const three = outcomes?.salary_after_3y_usd;
  if (!start || !three) return null;
  const delta = three - start;
  const pct = Math.round((delta / start) * 100);
  const fmt = isUK ? _fmtGBP : _fmtUSD;
  return {
    text: `Grads earn ${fmt(start)} at 15 months and ${fmt(three)} three years out — a ${pct}% climb in under two years.`,
    tone: pct >= 25 ? "positive" : "neutral",
  };
}

/** LEO 3y vs 5y comparison. */
function leoCompareVerdict(hs) {
  const l3 = hs?.leo3_median_gbp;
  const l5 = hs?.leo5_median_gbp;
  if (!l3 || !l5) return null;
  const delta = l5 - l3;
  const pct = Math.round((delta / l3) * 100);
  return {
    text: `Median pay grows from ${_fmtGBP(l3)} at 3 years to ${_fmtGBP(l5)} at 5 years — a further ${pct}% rise across the mid-career window.`,
    tone: pct >= 15 ? "positive" : "neutral",
  };
}

export default function Cluster4ROI({ ctx }) {
  const {
    d, uni, hs, c, ai,
    isHesa, isDaad, isScorecard,
    placementHasData, outcomesHasData,
    employerLifts, format,
  } = ctx;
  return (
    <ClusterMember cluster={4}>
      {/* ROI headline block */}
      <TeachingLearningCluster degree={d} variant="roi" />
      <OfficialEarningsNotice official={d.official_earnings} />
      <IntlEarningsPanel official={d.official_earnings_intl} />

      {placementHasData && (
      <Section id="placement" title="Placements & practical experience">
        {(() => {
          const meta = hs.course_meta || {};
          const hesaHasPlacement = meta.sandwich_placement === true;
          const hesaHasYearAbroad = meta.year_abroad_available === true;
          const placementIsFullyNull = d.placement && Object.values(d.placement).every(
            (v) => v == null || (Array.isArray(v) && v.length === 0)
              || (typeof v === "object" && v !== null && !Array.isArray(v) && Object.keys(v).length === 0)
          );
          const noEditorial = (isHesa || isDaad || isScorecard) && !d.placement;
          const noKnownPlacement = noEditorial || placementIsFullyNull;
          const softVal = (v) => v == null && noKnownPlacement ? "Not verified" : v;

          const availableVal = d.placement?.available === true ? "Yes"
            : d.placement?.available === false ? "No"
            : hesaHasPlacement ? "Yes · Sandwich placement"
            : noKnownPlacement ? "Not verified" : "—";

          const compulsoryVal = d.placement?.compulsory === true ? "Yes"
            : d.placement?.compulsory === false ? "Optional"
            : hesaHasPlacement ? "Optional"
            : noKnownPlacement ? "Not verified" : "—";

          const studyAbroadFullyNull = d.study_abroad && Object.values(d.study_abroad).every(
            (v) => v == null || (Array.isArray(v) && v.length === 0)
          );
          const studyAbroadVal = d.study_abroad?.available
            ? `Yes · ${(d.study_abroad.partner_universities || []).join(", ") || "partner unis"}`
            : hesaHasYearAbroad ? "Yes · Year abroad available"
            : ((isHesa || isDaad || isScorecard) && !d.study_abroad) || studyAbroadFullyNull ? "Not verified"
            : "—";

          const uniLink = meta.assessments_url || meta.learning_teaching_url || hs.course_page_source_url
            || (isDaad ? d.source_provenance?.url : null);
          return (
            <>
              <div className="grid md:grid-cols-2 gap-x-8">
                <DataRow k="Available" v={availableVal} />
                <DataRow k="Compulsory" v={compulsoryVal} />
                <DataRow k="% who secure" v={d.placement?.pct_secured != null ? `${(d.placement.pct_secured * 100).toFixed(0)}%` : (softVal(null) || "—")} />
                <DataRow k="Paid" v={d.placement?.paid === true ? "Yes" : d.placement?.paid === false ? "No" : (softVal(null) || "—")} />
                <DataRow k="Typical employers" v={(d.placement?.typical_employers || []).join(", ") || (softVal(null) || "—")} />
                <DataRow k="Avg. placement salary" v={d.placement?.average_salary_usd ? format(d.placement.average_salary_usd) : (softVal(null) || "—")} />
                <DataRow k="Extends degree" v={d.placement?.extends_degree === true ? "Yes" : d.placement?.extends_degree === false ? "No" : (softVal(null) || "—")} />
                <DataRow k="Study abroad" v={studyAbroadVal} />
              </div>
              {d.placement?.note && (
                <div
                  className="mt-3 rounded-md border border-amber-300 bg-amber-50/60 p-3 text-sm text-stone-800"
                  data-testid="placement-note"
                >
                  <b>{d.placement.note}</b>
                </div>
              )}
              {noEditorial && uniLink && (
                <div className="mt-3 text-xs text-stone-500" data-testid="placement-not-published-note">
                  {isDaad
                    ? "DAAD doesn't publish placement details per-course."
                    : "Discover Uni doesn't publish placement details per-course."}
                  {" "}
                  <a
                    href={uniLink.startsWith("http") ? uniLink : `https://${uniLink}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#4C1D95] hover:underline inline-flex items-center gap-1"
                  >
                    {isDaad ? "See on DAAD" : "See the official course page"} <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
            </>
          );
        })()}
        <Ev ev={d.placement?.evidence
          || (isHesa && (hs.course_meta?.sandwich_placement || hs.course_meta?.year_abroad_available)
            ? { source: "HESA Discover Uni · course_meta", confidence: "verified" }
            : null)} />
      </Section>
      )}

      {outcomesHasData && (
      <Section id="outcomes" title="Graduate outcomes">
        <AIInterpretationNote
          interpretation={ai.interpretation}
          sectionKey="outcomes"
          degreeId={d.id}
          dataAvailable={
            d.outcomes?.employment_rate != null ||
            d.outcomes?.highly_skilled_employment_pct != null ||
            d.outcomes?.median_starting_salary_usd != null ||
            d.outcomes?.salary_after_3y_usd != null ||
            (d.outcomes?.top_employers?.length ?? 0) > 0 ||
            (d.outcomes?.destinations?.top_employers?.length ?? 0) > 0 ||
            hs.employment_workstudy_pct != null ||
            hs.leo3_median_gbp != null
          }
        />
        <OutcomeScopeLegend />
        <SalaryProgressionWidget degree={d} />
        {(() => {
          const isUK = typeof d.id === "string" && d.id.startsWith("uk-");
          const v = progressionVerdict(d.outcomes, isUK);
          return v ? <ChartVerdict testId="verdict-salary-progression" text={v.text} tone={v.tone} /> : null;
        })()}

        {isHesa && hs.leo3_median_gbp && (c.tuition_intl_annual_gbp || uni?.tuition_intl_annual_gbp) && (
          <FeeVsSalaryCard
            salary_usd={Math.round(hs.leo3_median_gbp * 1.33)}
            salary_label="LEO3 median salary"
            salary_source="HESA LEO 3-year median"
            tuition_intl_usd={c.tuition_intl_annual_usd ?? uni?.tuition_intl_annual_usd}
            tuition_home_usd={c.tuition_domestic_annual_usd ?? uni?.tuition_domestic_annual_usd}
            duration_years={d.duration_years || 3}
            take_home_pct={0.72}
          />
        )}
        {(typeof d.id === "string" && d.id.startsWith("us-")) &&
         (uni?.scorecard_outcomes?.median_earnings_10yr_usd || uni?.scorecard_outcomes?.median_earnings_6yr_usd) &&
         (c.tuition_intl_annual_usd || uni?.tuition_intl_annual_usd) && (
          <FeeVsSalaryCard
            salary_usd={uni.scorecard_outcomes.median_earnings_10yr_usd || uni.scorecard_outcomes.median_earnings_6yr_usd}
            salary_label={uni.scorecard_outcomes.median_earnings_10yr_usd ? "Median earnings 10 yr post-entry" : "Median earnings 6 yr post-entry"}
            salary_source={uni.scorecard_outcomes.median_earnings_10yr_usd ? "US Dept of Education Scorecard, 10 years after entry" : "US Dept of Education Scorecard, 6 years after entry"}
            tuition_intl_usd={firstYearOnly(d.cost) ? null : c.tuition_intl_annual_usd ?? uni?.tuition_intl_annual_usd}
            tuition_home_usd={firstYearOnly(d.cost) ? null : c.tuition_domestic_annual_usd ?? uni?.tuition_domestic_annual_usd}
            home_label="In-state"
            intl_label="Out-of-state"
            duration_years={d.duration_years || 4}
            take_home_pct={0.75}
          />
        )}
        {(
          (isHesa && hs.leo3_median_gbp && (c.tuition_intl_annual_gbp || uni?.tuition_intl_annual_gbp)) ||
          ((typeof d.id === "string" && d.id.startsWith("us-")) &&
           (uni?.scorecard_outcomes?.median_earnings_10yr_usd || uni?.scorecard_outcomes?.median_earnings_6yr_usd) &&
           (c.tuition_intl_annual_usd || uni?.tuition_intl_annual_usd))
        ) && (
          <div className="-mt-2 mb-5 flex justify-end" data-testid="right-course-chip-wrap">
            <Link
              to="/specialists?focus=subjects-advisor"
              data-testid="right-course-chip"
              className="group inline-flex items-center gap-1.5 rounded-full border border-[#4C1D95]/30 bg-[#F5F0FF] px-3 py-1 text-[11px] text-[#4C1D95] hover:bg-[#EDE4FF] hover:border-[#4C1D95] transition"
              title="Talk to a subject advisor about whether this course is the right fit"
            >
              <span className="font-medium">Is this the right course?</span>
              <span className="text-[10px] text-[#4C1D95]/80 group-hover:text-[#4C1D95]">
                Book a subject advisor →
              </span>
            </Link>
          </div>
        )}
        {(() => {
          const outcomes = d.outcomes || {};
          const workOnlyPct = hs.employment_breakdown?.activity_pct?.work ?? null;
          const workStudyCombined = hs.employment_workstudy_pct ?? null;
          const empRate = outcomes.employment_rate != null
            ? outcomes.employment_rate * 100
            : workOnlyPct;
          const hsPct = outcomes.highly_skilled_employment_pct != null
            ? outcomes.highly_skilled_employment_pct * 100
            : (hs.top_graduate_jobs && hs.top_graduate_jobs.length
              ? (() => {
                  const byGroup = {};
                  for (const j of hs.top_graduate_jobs) {
                    const g = j.cah_group || "_default";
                    if (!byGroup[g]) byGroup[g] = 0;
                    if (j.highly_skilled) byGroup[g] += (j.percentage || 0);
                  }
                  const groups = Object.values(byGroup);
                  if (groups.length === 0) return null;
                  return groups.reduce((s, v) => s + v, 0) / groups.length;
                })()
              : null);
          const pgPct = outcomes.postgrad_study_pct != null
            ? outcomes.postgrad_study_pct * 100
            : hs.employment_breakdown?.activity_pct?.further_study ?? null;
          const respRate = outcomes.response_rate != null
            ? outcomes.response_rate * 100
            : hs.employment_breakdown?.response_rate_pct ?? null;
          const cohortSize = outcomes.cohort_size ?? hs.employment_sample_size ?? null;
          const salaryStart = outcomes.median_starting_salary_usd
            ? format(outcomes.median_starting_salary_usd)
            : (hs.salary_median_gbp != null ? `£${hs.salary_median_gbp.toLocaleString()}` : "—");
          const salary3y = outcomes.salary_after_3y_usd
            ? format(outcomes.salary_after_3y_usd)
            : outcomes.median_earnings_5yr_usd
              ? `${format(outcomes.median_earnings_5yr_usd)} (5y median · Scorecard)`
              : (hs.leo3_median_gbp != null ? `£${hs.leo3_median_gbp.toLocaleString()}` : "—");
          const pct = (v) => v == null ? "—" : `${Math.round(v)}%`;
          const _mkNote = (n) => n
            ? `Measured for this course cohort · n=${n}`
            : undefined;
          const _wrap = (val, sampleN) => (
            <>
              {val}
              <ScopeChip
                scope="course"
                note={_mkNote(sampleN)}
              />
            </>
          );
          return (
            <div className="grid md:grid-cols-2 gap-x-8" data-testid="outcomes-grid">
              <DataRow k="In work only" v={_wrap(pct(empRate), hs.employment_sample_size)} />
              <DataRow k="In work or further study" v={<>{pct(workStudyCombined)}<SmallSample n={hs.employment_sample_size} /><ScopeChip scope="course" note={_mkNote(hs.employment_sample_size)} /></>} />
              <DataRow k="Highly skilled" v={_wrap(pct(hsPct), hs.employment_sample_size)} />
              <DataRow k="Postgrad study" v={_wrap(pct(pgPct), hs.employment_sample_size)} />
              <DataRow k="Median starting salary" v={<>{salaryStart}<SmallSample n={hs.salary_sample_size} /><ScopeChip scope="course" note={_mkNote(hs.salary_sample_size)} /></>} />
              <DataRow k="Salary after 3y" v={<>{salary3y}<SmallSample n={hs.leo3_sample_size} /><ScopeChip scope="course" note={_mkNote(hs.leo3_sample_size)} /></>} />
              <DataRow k="Response rate" v={_wrap(pct(respRate), hs.employment_sample_size)} />
              <DataRow k="Cohort size" v={_wrap(cohortSize ?? "—", null)} />
              <DataRow k="Intl employment" v={_wrap(outcomes.international_employment_rate ? `${(outcomes.international_employment_rate * 100).toFixed(0)}%` : "—", null)} />
            </div>
          );
        })()}
        {d.outcomes?.top_occupations && (
          <div className="mt-3 text-sm"><b>Top occupations:</b> {d.outcomes.top_occupations.join(", ")}</div>
        )}

        {d.subject_area && <DegreeEventsInline subject={d.subject_area} />}

        {(() => {
          const dst = d.outcomes?.destinations;
          const _rawCurated = dst?.top_employers || [];
          const curatedEmps = _rawCurated
            .map((e) => (typeof e === "string" ? e : (e?.employer || e?.name || "")))
            .filter(Boolean);
          const fallbackEmps = (d.outcomes?.top_employers || [])
            .map((e) => (typeof e === "string" ? e : (e?.employer || e?.name || "")))
            .filter(Boolean);
          const employers = curatedEmps.length > 0 ? curatedEmps : fallbackEmps;
          if (employers.length === 0) return null;
          const usingCurated = curatedEmps.length > 0;
          const provSlug = dst?.provenance || "";
          const isEditorial =
            dst?.confidence === "editorial-synthesis" ||
            provSlug.startsWith("wua_editorial_");
          const provLabel = (() => {
            if (dst?.source) {
              const yr = dst.survey_year || "";
              const provider = (dst.source || "").split(" ")[0];
              return isEditorial
                ? `Editorial · ${provider} ${yr}`.trim()
                : `Verified ${yr} · ${provider}`.trim();
            }
            if (provSlug.startsWith("wua_editorial_")) {
              const rest = provSlug.replace("wua_editorial_", "");
              const parts = rest.split("_");
              const region = (parts[0] || "").toUpperCase();
              const wave = parts.slice(1).join(" ").replace(/wave(\d+)/, "Wave $1");
              return `WUA Editorial · ${region} ${wave}`.trim();
            }
            return null;
          })();
          return (
          <div className="mt-2 text-sm">
            <div className="flex items-baseline flex-wrap gap-2">
              <b>Top employers:</b>
              {provLabel && (
                <span
                  data-testid="degree-destinations-provenance-chip"
                  title={
                    (dst?.source || provSlug || "") +
                    (dst?.source_url ? ` — ${dst.source_url}` : "") +
                    (dst?.stamped_at ? ` — stamped ${String(dst.stamped_at).slice(0, 10)}` : "")
                  }
                  className={
                    "inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider rounded-full border px-2 py-0.5 " +
                    (isEditorial
                      ? "border-sky-300 bg-sky-50 text-sky-800"
                      : "border-emerald-400 bg-white text-emerald-800")
                  }
                >
                  <ShieldCheck className="h-2.5 w-2.5" aria-hidden />
                  {provLabel}
                </span>
              )}
              {dst?.signal_band && provSlug.startsWith("wua_editorial_") && (
                <span
                  data-testid="degree-destinations-signal-band"
                  title="How confident we are that this list reflects the true top-5 recruiters"
                  className={
                    "inline-flex items-center text-[10px] font-underground-sc tracking-wider rounded-full border px-2 py-0.5 " +
                    (dst.signal_band === "strong"
                      ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                      : "border-amber-300 bg-amber-50 text-amber-800")
                  }
                >
                  Signal: {dst.signal_band}
                </span>
              )}
              {dst?.sample_size && (
                <span
                  data-testid="degree-destinations-sample-size"
                  title="Number of graduates in the surveyed cohort"
                  className="text-[10px] font-underground-sc tracking-wider text-stone-500"
                >
                  n={dst.sample_size}
                </span>
              )}
            </div>
            <div
              className="mt-1.5 flex flex-wrap gap-1.5"
              data-testid="degree-top-employers-chiplist"
            >
              {(() => {
                const isUk = typeof d.id === "string" && d.id.startsWith("uk-");
                const isUs = typeof d.id === "string" && d.id.startsWith("us-");
                const country = isUk ? "UK" : isUs ? "US" : null;
                const slugify = (name) =>
                  (name || "")
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-+|-+$/g, "");
                return employers.map((name, i) => {
                  const slug = slugify(name);
                  const lift = employerLifts?.[slug];
                  if (country && slug) {
                    const refParams = new URLSearchParams();
                    if (country === "US") refParams.set("country", "US");
                    if (d.subject_area) refParams.set("ref_subject", d.subject_area);
                    if (d.id) refParams.set("ref_degree_id", d.id);
                    const qs = refParams.toString();
                    return (
                      <Link
                        key={`${slug}-${i}`}
                        to={`/employers/${slug}${qs ? `?${qs}` : ""}`}
                        data-testid={`degree-employer-chip-${slug}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#4C1D95]/25 bg-[#F5F0FF] text-[#4C1D95] hover:bg-[#EDE4FF] hover:border-[#4C1D95] transition pl-1 pr-2.5 py-0.5 text-xs"
                        title={
                          lift
                            ? `${name} grads see a +${lift.lift_pct}% salary lift 3→5yr — click for feeder courses`
                            : `See how ${name} grads' salaries progress`
                        }
                      >
                        <FirmLogo
                          slug={slug}
                          name={name}
                          IconFallback={Briefcase}
                          size={18}
                        />
                        {name}
                        {lift?.lift_pct != null && (
                          <span
                            className="font-mono text-[10px] text-emerald-700 font-semibold"
                            data-testid={`degree-employer-chip-${slug}-lift`}
                          >
                            ↗ +{lift.lift_pct}%
                          </span>
                        )}
                        <span className="text-[9px] opacity-70" aria-hidden>→</span>
                      </Link>
                    );
                  }
                  return (
                    <span
                      key={`${slug || "emp"}-${i}`}
                      className="inline-flex items-center rounded-full border border-stone-200 bg-stone-50 text-stone-700 px-2.5 py-0.5 text-xs"
                    >
                      {name}
                    </span>
                  );
                });
              })()}
            </div>
            {dst?.rationale && provSlug.startsWith("wua_editorial_") && (
              <p
                data-testid="degree-destinations-rationale"
                className="mt-2 text-xs text-stone-600 italic border-l-2 border-[#4C1D95]/25 pl-3"
              >
                <span className="not-italic font-semibold text-stone-700">
                  Why these five:
                </span>{" "}
                {dst.rationale}
              </p>
            )}
            {usingCurated && (
              <p
                data-testid="degree-destinations-methodology-note"
                className="mt-2 text-[11px] text-stone-500"
              >
                Curated per-subject by WhichUniversityAdvisor editors from
                employer LinkedIn analytics, university career-office
                reports and known industry pipelines. See{" "}
                <Link to="/about" className="text-[#4C1D95] hover:underline">
                  our methodology
                </Link>
                .
              </p>
            )}
            <PlacementsDeepDiveCard degree={d} />
          </div>
          );
        })()}
        {isDaad && !d.outcomes?.employment_rate && (
          <SubjectAggregatePanel
            testId="subject-wide-context-de"
            subjectLabel={d.subject_area || "German subject baseline"}
            eyebrow="Germany national baseline"
            zoomOutCopy="how the average grad in this subject fares nationally:"
          >
            <DeSubjectBaseline degree={d} />
            <div className="mt-3 text-xs text-stone-500" data-testid="daad-outcomes-note">
              Germany doesn't publish per-programme employment stats federally
              — for graduate outcomes check the university's{" "}
              <em>Absolventenbefragung</em> (graduate survey) or the DAAD
              programme page.
              {d.source_provenance?.url && (
                <>
                  {" "}
                  <a
                    href={d.source_provenance.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#4C1D95] hover:underline inline-flex items-center gap-1"
                  >
                    Open on DAAD <ExternalLink className="h-3 w-3" />
                  </a>
                </>
              )}
            </div>
          </SubjectAggregatePanel>
        )}
        <LeoComparison hesa={d.hesa_stats} employerLifts={employerLifts} official={d.official_leo5} />
        {(() => {
          const v = d.official_leo5 ? null : leoCompareVerdict(d.hesa_stats);
          return v ? <ChartVerdict testId="verdict-leo-compare" text={v.text} tone={v.tone} /> : null;
        })()}
        {typeof d.id === "string" && d.id.startsWith("us-") && (
          <UsEarningsComparison outcomes={d.outcomes} employerLifts={employerLifts} />
        )}
        {isScorecard && !d.outcomes?.employment_rate && (
          <SubjectAggregatePanel
            testId="subject-wide-context-us"
            subjectLabel={d.subject_area || "US subject baseline"}
            eyebrow="US national baseline"
            zoomOutCopy="how the average grad in this subject fares nationally:"
          >
            <UsSubjectBaseline degree={d} />
          </SubjectAggregatePanel>
        )}
        {d.hesa_stats?.leo_sec && (
          <SubjectAggregatePanel
            testId="subject-wide-context-leo-region"
            subjectLabel={(() => {
              const s = d.hesa_stats.leo_sec;
              return s?.cah_matched || s?.cah_full || "UK subject group";
            })()}
            eyebrow="National LEO earnings by UK region"
            zoomOutCopy={(() => {
              const s = d.hesa_stats.leo_sec;
              const label = s?.cah_matched || s?.cah_full;
              return label
                ? <>nationally, graduates in <strong>{label}</strong> earn:</>
                : "national salary breakdown by region of work:";
            })()}
          >
            <LeoSectorChart leoSec={d.hesa_stats.leo_sec} />
            {(() => {
              const v = leoSectorVerdict(d.hesa_stats.leo_sec);
              return v ? <ChartVerdict testId="verdict-leo-sector" text={v.text} tone={v.tone} /> : null;
            })()}
          </SubjectAggregatePanel>
        )}
        <EmploymentBreakdownBlock emp={d.hesa_stats?.employment_breakdown} />
        <TopGraduateJobs jobs={d.hesa_stats?.top_graduate_jobs} />

        <PostgradSiblingsWidget degreeId={d.id} level={d.level} />

        <DegreeSourcesPanel degree={d} />
        <Ev ev={d.outcomes?.evidence
          || (isHesa && (hs.salary_median_gbp != null || hs.employment_workstudy_pct != null)
            ? { source: "HESA Discover Uni · Graduate Outcomes survey (15 months post-grad)", confidence: "verified", url: "https://discoveruni.gov.uk/" }
            : null)} />
      </Section>
      )}
      <SimilarCoursesLink subject={d.subject_area || d.subject} />
    </ClusterMember>
  );
}
